// ============================================================
// actedAs (v9.5.3): who has acted as whom THIS SESSION, from the seats.
//
// `actingBy` names one combatant per account - the one their newest seat
// is set to now. A called test wants more: a player with two characters
// should be asked for both. Each seat keeps a short list of every
// combatant it has acted as (`SessionPlayer.actedAs`, a stringified id
// list), and this reads it back. Pure.
// ============================================================
import type { SeatLike } from './actingBy';

/** How many a seat remembers: the latest, in order. */
export const ACTED_AS_CAP = 8;

/** `id` appended (moved to the end when already there), the oldest dropped past the cap. */
export function pushActed(list: readonly string[], id: string): string[] {
  const next = [...list.filter((v) => v !== id), id];
  return next.slice(Math.max(0, next.length - ACTED_AS_CAP));
}

/** The stored text back into ids; [] for anything that is not a list of strings. */
export function readActedAs(raw: unknown): string[] {
  if (typeof raw !== 'string' || raw === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string' && v !== '') : [];
  } catch {
    return [];
  }
}

interface ActedSeat extends SeatLike {
  actedAs?: unknown;
}

/** Everyone a seat has acted as: its remembered list, plus who it acts as now. */
function actedOf(s: ActedSeat): string[] {
  const now = typeof s.actingAs === 'string' && s.actingAs ? [s.actingAs] : [];
  return [...readActedAs(s.actedAs), ...now];
}

/** combatantId -> the accounts whose ANY seat acted as them (each once, by first sight). */
export function actedByFrom(seats: readonly ActedSeat[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const s of seats.filter((seat) => !!seat.uid)) {
    for (const cid of actedOf(s)) {
      const list = (out[cid] ??= []);
      if (!list.includes(s.uid as string)) list.push(s.uid as string);
    }
  }
  return out;
}

/**
 * Who is prompted for each person asked, by default: everyone who acted as
 * them this session, never `exceptUid` (the GM's own); failing that, the
 * account acting as them now; [] = the GM rolls them.
 */
export function promptDefaults(
  who: readonly string[],
  actedBy: Readonly<Record<string, string[]>>,
  actingBy: Readonly<Record<string, string>>,
  exceptUid: string | null,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const cid of who) {
    const acted = (actedBy[cid] ?? []).filter((uid) => uid !== exceptUid);
    const now = Object.entries(actingBy).find(([uid, c]) => c === cid && uid !== exceptUid)?.[0];
    out[cid] = acted.length > 0 ? acted : now ? [now] : [];
  }
  return out;
}

/**
 * The figures this seat calls its own (the take-cover cue, 2026-10-25): every person its account has acted as
 * this session, and the one it acts as now. Empty for a seat with no account.
 */
export function ownFigureIds(
  actedBy: Readonly<Record<string, readonly string[]>>,
  actingBy: Readonly<Record<string, string>>,
  uid: string | null | undefined,
): Set<string> {
  const own = new Set<string>();
  if (!uid) return own;
  for (const [cid, uids] of Object.entries(actedBy)) if (uids.includes(uid)) own.add(cid);
  if (actingBy[uid]) own.add(actingBy[uid]);
  return own;
}
